import { CANVAS_WIDTH } from "../../game/constants";
import { easeOutCubic } from "../../shared/easing";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import type { RankedTier } from "../../versus/ranked";
import type { SoloMode } from "../../game/types";
import type { drawButton } from "../ui";
import {
  GAME_FONT,
  mFont,
  mFontPlain,
  menuButtonColumns,
  menuBtnHLg,
  roundedRectPath,
  compactCount,
  resolveSoloLeaderboardSurfaceState,
  MENU_TEXT_PRIMARY,
  MENU_TEXT_SECONDARY,
  MENU_PANEL_FILL,
  MENU_PANEL_STROKE,
} from "../renderConstants";
import { isMobileViewport } from "../../game/platform";
import type { LeaderboardScrollConfig } from "../leaderboardScrollController";

// ── Types ────────────────────────────────────────────────────────────────

/** Mutable animation state owned by the renderer, passed by reference. */
export interface LeaderboardAnimState {
  leaderboardScreenEnterTime: number;
}

export interface LeaderboardDeps {
  ctx: CanvasRenderingContext2D;
  nowMs: () => number;
  nowSec: () => number;
  animState: LeaderboardAnimState;
  drawBtnStagger: (
    rect: Rect,
    label: string,
    index: number,
    options?: Parameters<typeof drawButton>[3],
  ) => void;
  drawHourglass: (cx: number, cy: number, size: number, color: string) => void;
  drawLeaderboardTab: (
    rect: Rect,
    label: string,
    active: boolean,
    accent: string,
    fontPx?: number,
  ) => void;
  drawSoloLeaderboardModeChips: (
    uiRects: UiRects,
    originX: number,
    originY: number,
    width: number,
    mobile: boolean,
    selectedMode: SoloMode,
  ) => number;
  leaderboardStaggerAlpha: (
    index: number,
    delayMs: number,
    durationMs: number,
  ) => number;
  leaderboardTierColor: (tier: RankedTier) => string;
  resolveLeaderboardScroll: (config: LeaderboardScrollConfig) => number;
}

// ── Extracted screen function ────────────────────────────────────────────

export function drawLeaderboardScreen(
  deps: LeaderboardDeps,
  model: RenderModel,
): UiRects {
  const ctx = deps.ctx;
  const centerX = CANVAS_WIDTH / 2;
  const menuCols = menuButtonColumns(centerX);

  ctx.fillStyle = MENU_TEXT_PRIMARY;
  ctx.font = `bold 12px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("RANKINGS", centerX, 132);

  const tabW = 88;
  const tabH = 22;
  const tabGap = 10;
  const leaderboardTabSolo: Rect = { x: centerX - tabW - tabGap / 2, y: 160, w: tabW, h: tabH };
  const leaderboardTabVersus: Rect = { x: centerX + tabGap / 2, y: 160, w: tabW, h: tabH };
  const uiRects: UiRects = { leaderboardTabSolo, leaderboardTabVersus };
  deps.drawLeaderboardTab(leaderboardTabSolo, "SOLO", model.leaderboardTab === "solo", "#5c8cd4");
  deps.drawLeaderboardTab(leaderboardTabVersus, "VERSUS", model.leaderboardTab === "versus", "#ff8a7c");

  const btnH = menuBtnHLg();
  const btnGap = 8;
  const panelW = menuCols.fullW;
  const panelX = menuCols.fullX;
  const panelY = 192;
  const panelBottom = isMobileViewport() ? 504 : 466;
  const panelH = panelBottom - panelY - btnH - btnGap;
  roundedRectPath(ctx, panelX, panelY, panelW, panelH, 10);
  ctx.fillStyle = MENU_PANEL_FILL;
  ctx.fill();
  roundedRectPath(ctx, panelX, panelY, panelW, panelH, 10);
  ctx.strokeStyle = MENU_PANEL_STROKE;
  ctx.lineWidth = 1;
  ctx.stroke();

  if (model.leaderboardTab === "solo") {
    const soloLeaderboard = resolveSoloLeaderboardSurfaceState(
      model.leaderboard.slice(0, 100),
      model.leaderboardLoading,
      model.leaderboardMode,
      model.leaderboardDisplayMode
    );
    const entries = soloLeaderboard.entries;
    const headerY = deps.drawSoloLeaderboardModeChips(
      uiRects,
      panelX + 12,
      panelY + 12,
      panelW - 24,
      isMobileViewport(),
      model.selectedSoloLeaderboardMode
    ) + 10;
    const lineHeight = isMobileViewport() ? 22 : 18;
    const headerGap = 18;

    ctx.fillStyle = MENU_TEXT_SECONDARY;
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "left";
    ctx.fillText("#", panelX + 12, headerY);
    ctx.fillText("NAME", panelX + 42, headerY);
    ctx.textAlign = "right";
    ctx.fillText("SCORE", panelX + panelW - 12, headerY);
    ctx.textAlign = "left";
    ctx.strokeStyle = "rgba(130,170,255,0.25)";
    ctx.beginPath();
    ctx.moveTo(panelX + 10, headerY + 11);
    ctx.lineTo(panelX + panelW - 10, headerY + 11);
    ctx.stroke();

    const listTop = headerY + headerGap;
    const listH = panelH - (listTop - panelY) - 8;
    uiRects.leaderboardScrollViewport = { x: panelX + 4, y: listTop, w: panelW - 8, h: listH };

    if (entries.length === 0 && soloLeaderboard.loading) {
      deps.drawHourglass(centerX, panelY + panelH / 2 - 2, 24, "#69e6ff");
      ctx.fillStyle = MENU_TEXT_SECONDARY;
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.fillText("LOADING...", centerX, panelY + panelH / 2 + 18);
    } else if (entries.length === 0) {
      ctx.fillStyle = MENU_TEXT_SECONDARY;
      ctx.font = mFontPlain(8);
      ctx.textAlign = "center";
      ctx.fillText("NO SCORES YET", centerX, panelY + panelH / 2 - 8);
    } else {
      const totalHeight = entries.length * lineHeight;
      const maxOffset = Math.max(0, totalHeight - listH);
      const scrollSpeed = 30;
      const scrollOffset = deps.resolveLeaderboardScroll({
        kind: "continuous",
        maxOffset,
        speedPxPerSec: scrollSpeed
      });

      ctx.save();
      ctx.beginPath();
      ctx.rect(panelX + 4, listTop, panelW - 8, listH);
      ctx.clip();
      for (let i = 0; i < entries.length; i += 1) {
        const item = entries[i];
        const rowY = listTop + i * lineHeight - scrollOffset;
        if (rowY + lineHeight < listTop || rowY > listTop + listH) continue;

        const rowAlpha = deps.leaderboardStaggerAlpha(i, 50, 200);
        if (rowAlpha <= 0) continue;
        if (rowAlpha < 1) {
          ctx.save();
          ctx.globalAlpha = easeOutCubic(rowAlpha);
          ctx.translate(20 * (1 - easeOutCubic(rowAlpha)), 0);
        }

        roundedRectPath(ctx, panelX + 8, rowY, panelW - 16, lineHeight - 4, 5);
        ctx.fillStyle = i % 2 === 0 ? "rgba(110,76,188,0.18)" : "rgba(68,98,168,0.14)";
        ctx.fill();

        ctx.font = mFont("bold", 7);
        ctx.textAlign = "left";
        if (i < 3) {
          const rankColor = i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : "#cd7f32";
          const rankPulse = 0.3 + 0.7 * Math.sin(deps.nowMs() / 800 + i * 0.8);
          ctx.save();
          ctx.shadowColor = rankColor;
          ctx.shadowBlur = 4 + 4 * rankPulse;
          ctx.fillStyle = rankColor;
          ctx.fillText(`${i + 1}.`, panelX + 12, rowY + 4);
          ctx.restore();
        } else {
          ctx.fillStyle = MENU_TEXT_PRIMARY;
          ctx.fillText(`${i + 1}.`, panelX + 12, rowY + 4);
        }

        ctx.fillStyle = MENU_TEXT_PRIMARY;
        ctx.fillText(item.name, panelX + 42, rowY + 4);

        ctx.textAlign = "right";
        ctx.fillStyle = i === 0 ? "#ffd700" : MENU_TEXT_PRIMARY;
        ctx.fillText(Math.floor(item.score).toLocaleString(), panelX + panelW - 12, rowY + 4);

        if (rowAlpha < 1) ctx.restore();
      }
      ctx.restore();
    }
  } else {
    const entries = model.versusLeaderboard.slice(0, 100);
    const rankContext = model.versusRankContext;
    const player = rankContext?.player ?? null;
    const currentUserId = model.authState.user?.id ?? null;
    const signedInRankedUser = currentUserId != null && currentUserId !== "guest-local";
    const playerInTopList = currentUserId != null && entries.some((entry) => entry.userId === currentUserId);
    const showPinnedNeighbors = player != null
      && player.rank != null
      && !playerInTopList
      && (rankContext?.neighbors.length ?? 0) > 0;
    const summaryX = panelX + 10;
    const summaryY = panelY + 10;
    const summaryW = panelW - 20;
    const summaryH = isMobileViewport() ? 86 : 74;

    roundedRectPath(ctx, summaryX, summaryY, summaryW, summaryH, 8);
    ctx.fillStyle = "rgba(10,18,34,0.82)";
    ctx.fill();
    roundedRectPath(ctx, summaryX, summaryY, summaryW, summaryH, 8);
    ctx.strokeStyle = "rgba(122,164,230,0.35)";
    ctx.lineWidth = 1;
    ctx.stroke();

    if (player != null) {
      const accent = deps.leaderboardTierColor(player.tier);
      roundedRectPath(ctx, summaryX + 8, summaryY + 8, 78, summaryH - 16, 8);
      ctx.fillStyle = "rgba(18,28,48,0.95)";
      ctx.fill();
      roundedRectPath(ctx, summaryX + 8, summaryY + 8, 78, summaryH - 16, 8);
      ctx.strokeStyle = accent;
      ctx.stroke();

      ctx.fillStyle = accent;
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(player.tier.toUpperCase(), summaryX + 47, summaryY + 16);
      ctx.fillStyle = "#f6fbff";
      ctx.font = mFont("bold", 11);
      ctx.fillText(String(player.rating), summaryX + 47, summaryY + 32);

      const infoX = summaryX + 96;
      const provisionalText = player.provisional
        ? `PROVISIONAL ${Math.min(10, player.rankedGames)}/10`
        : `${player.rankedGames} RANKED GAMES`;
      const rankText = player.rank == null
        ? `UNRANKED / ${rankContext?.totalEligiblePlayers ?? 0}`
        : `#${player.rank} / ${rankContext?.totalEligiblePlayers ?? 0}`;
      const percentileText = player.percentile == null ? "PCTL --" : `PCTL ${player.percentile}`;

      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#d8ebff";
      ctx.font = mFont("bold", 6);
      ctx.fillText(rankText, infoX, summaryY + 14);
      ctx.fillStyle = "#94b6da";
      ctx.font = mFontPlain(5);
      ctx.fillText(percentileText, infoX, summaryY + 28);
      ctx.fillStyle = "#f0f7ff";
      ctx.font = mFont("bold", 6);
      ctx.fillText(`W ${player.rankedWins}  L ${player.rankedLosses}  DC ${player.rankedDisconnects}`, infoX, summaryY + 42);
      ctx.fillStyle = player.provisional ? "#ffb37a" : "#7ce5d8";
      ctx.font = mFont("bold", 5);
      ctx.fillText(provisionalText, infoX, summaryY + 56);
    } else {
      ctx.fillStyle = "#9cc2ff";
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const summaryMessage = signedInRankedUser
        ? "PLAY QUICKMATCH TO PLACE ON THE LADDER"
        : "SIGN IN TO PLACE ON THE LADDER";
      ctx.fillText(summaryMessage, summaryX + summaryW / 2, summaryY + summaryH / 2 - 8);
      ctx.fillStyle = "#6f8db2";
      ctx.font = mFontPlain(5);
      ctx.fillText("VERSUS RANK USES VALIDATED QUICKMATCH RESULTS ONLY", summaryX + summaryW / 2, summaryY + summaryH / 2 + 8);
    }

    const headerY = summaryY + summaryH + 12;
    ctx.fillStyle = MENU_TEXT_SECONDARY;
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("#", panelX + 12, headerY);
    ctx.fillText("NAME", panelX + 40, headerY);
    ctx.textAlign = "right";
    ctx.fillText("RATING", panelX + panelW - 12, headerY);
    ctx.textAlign = "left";
    ctx.strokeStyle = "rgba(130,170,255,0.25)";
    ctx.beginPath();
    ctx.moveTo(panelX + 10, headerY + 11);
    ctx.lineTo(panelX + panelW - 10, headerY + 11);
    ctx.stroke();

    const listTop = headerY + 18;
    const pinnedStripH = showPinnedNeighbors ? 48 : 0;
    const listH = panelH - (listTop - panelY) - 8 - pinnedStripH;
    const rowH = isMobileViewport() ? 22 : 18;
    uiRects.leaderboardScrollViewport = { x: panelX + 4, y: listTop, w: panelW - 8, h: listH };

    if (entries.length === 0 && model.versusLeaderboardLoading) {
      deps.drawHourglass(centerX, listTop + listH / 2 - 4, 24, "#ff8a7c");
      ctx.fillStyle = MENU_TEXT_SECONDARY;
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.fillText("LOADING LADDER...", centerX, listTop + listH / 2 + 18);
    } else if (entries.length === 0) {
      ctx.fillStyle = MENU_TEXT_SECONDARY;
      ctx.font = mFontPlain(7);
      ctx.textAlign = "center";
      ctx.fillText("NO RANKED PLAYERS YET", centerX, listTop + listH / 2 - 6);
    } else {
      const totalHeight = entries.length * rowH;
      const maxOffset = Math.max(0, totalHeight - listH);
      const scrollSpeed = 26;
      const scrollOffset = deps.resolveLeaderboardScroll({
        kind: "continuous",
        maxOffset,
        speedPxPerSec: scrollSpeed
      });

      ctx.save();
      ctx.beginPath();
      ctx.rect(panelX + 4, listTop, panelW - 8, listH);
      ctx.clip();
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        const rowY = listTop + i * rowH - scrollOffset;
        if (rowY + rowH < listTop || rowY > listTop + listH) continue;

        const highlighted = currentUserId != null && entry.userId === currentUserId;
        roundedRectPath(ctx, panelX + 8, rowY, panelW - 16, rowH - 4, 5);
        ctx.fillStyle = highlighted
          ? "rgba(0,255,206,0.12)"
          : i % 2 === 0 ? "rgba(110,76,188,0.18)" : "rgba(68,98,168,0.14)";
        ctx.fill();
        if (highlighted) {
          roundedRectPath(ctx, panelX + 8, rowY, panelW - 16, rowH - 4, 5);
          ctx.strokeStyle = "rgba(0,255,220,0.65)";
          ctx.stroke();
        }

        ctx.font = mFont("bold", 7);
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillStyle = entry.rank === 1 ? "#ffd700" : entry.rank === 2 ? "#c0c0c0" : entry.rank === 3 ? "#cd7f32" : MENU_TEXT_PRIMARY;
        ctx.fillText(`${entry.rank}.`, panelX + 12, rowY + 4);
        ctx.fillStyle = highlighted ? "#8bfff5" : deps.leaderboardTierColor(entry.tier);
        ctx.fillText(entry.displayName, panelX + 40, rowY + 4);
        ctx.textAlign = "right";
        ctx.fillStyle = highlighted ? "#7effff" : "#e6f4ff";
        ctx.fillText(compactCount(entry.rating), panelX + panelW - 12, rowY + 4);
      }
      ctx.restore();
    }

    if (showPinnedNeighbors && rankContext != null) {
      const stripY = panelY + panelH - 8 - pinnedStripH;
      const stripGap = 6;
      const stripCardW = Math.floor((panelW - 20 - stripGap * 2) / 3);
      const stripEntries = rankContext.neighbors.slice(-3);
      for (let i = 0; i < stripEntries.length; i += 1) {
        const entry = stripEntries[i];
        const cardX = panelX + 10 + i * (stripCardW + stripGap);
        roundedRectPath(ctx, cardX, stripY, stripCardW, 40, 6);
        ctx.fillStyle = currentUserId != null && entry.userId === currentUserId
          ? "rgba(0,255,206,0.12)"
          : "rgba(12,22,38,0.95)";
        ctx.fill();
        roundedRectPath(ctx, cardX, stripY, stripCardW, 40, 6);
        ctx.strokeStyle = currentUserId != null && entry.userId === currentUserId
          ? "rgba(0,255,220,0.7)"
          : "rgba(92,140,212,0.45)";
        ctx.stroke();
        ctx.fillStyle = "#8fb0d8";
        ctx.font = mFontPlain(4);
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(`#${entry.rank}`, cardX + 6, stripY + 6);
        ctx.fillStyle = currentUserId != null && entry.userId === currentUserId ? "#8bfff5" : "#d8ebff";
        ctx.font = mFont("bold", 5);
        ctx.fillText(entry.displayName.slice(0, 10), cardX + 6, stripY + 16);
        ctx.textAlign = "right";
        ctx.fillStyle = deps.leaderboardTierColor(entry.tier);
        ctx.fillText(String(entry.rating), cardX + stripCardW - 6, stripY + 16);
      }
    }
  }

  const mainMenu: Rect = { x: menuCols.fullX, y: panelY + panelH + btnGap, w: menuCols.fullW, h: btnH };
  deps.drawBtnStagger(mainMenu, "BACK", 0, { fill: "#1a2a4c", stroke: "#5c8cd4", font: mFont("bold", 9) });

  return { ...uiRects, mainMenu };
}
